/* ==========================================================================
   WSPR home "Whisper" – assets/wspr-home.js (index template only, defer)
   One passive scroll listener + requestAnimationFrame.
   - [data-wspr-progress]: writes --p (pinned progress), --pv (view progress)
     and --yv (scrolled distance past the top, in viewport heights). CSS does the rest.
     data-wspr-on="0.7"  → toggles .is-on when --p >= 0.7
     data-wspr-flip="x"  → section counts as "light" for the header once --yv >= x
   - [data-wspr-reveal]: .is-in when it enters the viewport (IntersectionObserver).
   - Header: toggles .wspr-hd--light on the home header depending on the
     [data-wspr-theme] section under the header's vertical midpoint; positions
     the blend logo pair over the theme's logo link and hides it when a drawer
     or modal covers it.
   - Top picks prev/next, room hotspots.
   ========================================================================== */
(function () {
  'use strict';

  var d = document;
  var w = window;
  var reduce = !!(w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var clamp = function (v) { return v < 0 ? 0 : v > 1 ? 1 : v; };

  var progEls = [];
  var themed = [];
  var header = null;
  var blend = null;
  var mark = null;
  var cache = new WeakMap();
  var vh = w.innerHeight;
  var queued = false;
  var logoPt = null;
  var bound = false;

  function init() {
    progEls = [].slice.call(d.querySelectorAll('[data-wspr-progress]'));
    themed = [].slice.call(d.querySelectorAll('[data-wspr-theme]'));
    header = d.querySelector('.wspr-home-header');
    blend = d.querySelector('.wspr-logo-blend');
    mark = d.querySelector('.wspr-logo-mark');

    function setVar(el, name, val, store) {
      if (store[name] !== val) {
        store[name] = val;
        el.style.setProperty(name, val);
      }
    }

    function updateProgress() {
      for (var i = 0; i < progEls.length; i++) {
        var el = progEls[i];
        var r = el.getBoundingClientRect();
        var store = cache.get(el);
        if (!store) { store = {}; cache.set(el, store); }
        // skip work for sections far away from the viewport (but still settle them once)
        if ((r.bottom < -vh || r.top > vh * 2) && store.settled) continue;
        var range = r.height - vh;
        var yv = -r.top / vh;
        var p = range > 0 ? clamp(-r.top / range) : clamp((vh - r.top) / (vh + r.height));
        var pv = clamp((vh - r.top) / (vh + r.height));
        if (!reduce) {
          setVar(el, '--p', p.toFixed(4), store);
          setVar(el, '--pv', pv.toFixed(4), store);
          setVar(el, '--yv', Math.max(-3, Math.min(6, yv)).toFixed(4), store);
        }
        store.settled = r.bottom < -vh || r.top > vh * 2;
        var on = el.getAttribute('data-wspr-on');
        if (on !== null) el.classList.toggle('is-on', reduce || p >= parseFloat(on));
        var flip = el.getAttribute('data-wspr-flip');
        if (flip !== null) {
          var th = !reduce && yv >= parseFloat(flip) ? 'light' : 'dark';
          if (el.getAttribute('data-wspr-theme') !== th) el.setAttribute('data-wspr-theme', th);
        }
      }
    }

    function updateHeader() {
      if (!header) return;
      var hr = header.getBoundingClientRect();
      var mid = hr.top + hr.height / 2;
      var theme = 'dark';
      for (var i = 0; i < themed.length; i++) {
        var r = themed[i].getBoundingClientRect();
        if (r.top <= mid && r.bottom > mid) { theme = themed[i].getAttribute('data-wspr-theme'); break; }
      }
      header.classList.toggle('wspr-hd--light', theme === 'light');
    }

    function placeLogo() {
      if (!header || !blend) return;
      var link = header.querySelector('.xo-header__center a');
      if (!link) return;
      var r = link.getBoundingClientRect();
      if (!r.width || !r.height) return;
      logoPt = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      [blend, mark].forEach(function (el) {
        if (!el) return;
        el.style.setProperty('--wspr-lx', logoPt.x.toFixed(1) + 'px');
        el.style.setProperty('--wspr-ly', logoPt.y.toFixed(1) + 'px');
      });
    }

    function checkCovered() {
      if (!header || !blend || !logoPt) return;
      var el = d.elementFromPoint(logoPt.x, logoPt.y);
      var covered = !el || !header.contains(el);
      blend.classList.toggle('is-covered', covered);
      if (mark) mark.classList.toggle('is-covered', covered);
    }

    function revealSafety() {
      var pending = d.querySelectorAll('[data-wspr-reveal]:not(.is-in)');
      for (var i = 0; i < pending.length; i++) {
        if (pending[i].getBoundingClientRect().top < vh * 0.92) pending[i].classList.add('is-in');
      }
    }

    function frame() {
      queued = false;
      revealSafety();
      updateProgress();
      updateHeader();
      checkCovered();
    }

    function queue() {
      if (queued) return;
      queued = true;
      w.requestAnimationFrame(frame);
    }

    if (!bound) {
    bound = true;
    w.addEventListener('scroll', queue, { passive: true });
    w.addEventListener('resize', function () {
      vh = w.innerHeight;
      placeLogo();
      queue();
    }, { passive: true });
    w.addEventListener('load', function () { placeLogo(); queue(); });
    // drawers / modals open without scrolling: re-check the logo cover state now and then
    d.addEventListener('click', function () { setTimeout(queue, 50); setTimeout(queue, 400); }, true);
    d.addEventListener('keyup', function () { setTimeout(queue, 50); }, true);
    setInterval(function () { if (!d.hidden) { placeLogo(); checkCovered(); } }, 600);
    }

    placeLogo();
    frame();

    /* ---------- enter reveals ---------- */
    var reveals = [].slice.call(d.querySelectorAll('[data-wspr-reveal]'));
    if (reduce || !('IntersectionObserver' in w)) {
      reveals.forEach(function (el) { el.classList.add('is-in'); });
    } else {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
      reveals.forEach(function (el) { if (!el.classList.contains('is-in')) io.observe(el); });
    }

    /* ---------- top picks prev / next ---------- */
    [].slice.call(d.querySelectorAll('[data-wspr-scroll]')).forEach(function (btn) {
      if (btn.hasAttribute('data-wspr-bound')) return;
      btn.setAttribute('data-wspr-bound', '');
      btn.addEventListener('click', function () {
        var track = d.getElementById(btn.getAttribute('aria-controls'));
        if (!track) return;
        var card = track.firstElementChild;
        var step = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 24) : track.clientWidth;
        track.scrollBy({ left: step * parseInt(btn.getAttribute('data-wspr-scroll'), 10), behavior: reduce ? 'auto' : 'smooth' });
      });
    });

    /* ---------- room hotspots ---------- */
    [].slice.call(d.querySelectorAll('[data-wspr-spot]')).forEach(function (dot) {
      if (dot.hasAttribute('data-wspr-bound')) return;
      dot.setAttribute('data-wspr-bound', '');
      dot.addEventListener('click', function () {
        var spot = dot.parentElement;
        var open = !spot.classList.contains('is-open');
        [].slice.call(spot.parentElement.querySelectorAll('.wspr-spot.is-open')).forEach(function (s) {
          s.classList.remove('is-open');
          var b = s.querySelector('[data-wspr-spot]');
          if (b) b.setAttribute('aria-expanded', 'false');
        });
        spot.classList.toggle('is-open', open);
        dot.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init);
  else init();

  // theme editor: re-init after a section is re-rendered
  d.addEventListener('shopify:section:load', function () { init(); });
})();
